/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type KeyboardEvent, type MouseEvent } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useAtom, useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { token } from '@atlaskit/tokens';

import { availableSitesAtom, cloudIdAtom, orgIdAtom } from '../../state';

import { messages } from './messages';
import type { AvailableSite } from './types';

const styles = cssMap({
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderRadius: '6px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderWidth: '1.5px',
		borderStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#505258',
		backgroundColor: 'transparent',
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			borderColor: '#6B6E76',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			color: '#F0F1F2',
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
});

export const SiteSwitcher = (): JSX.Element | null => {
	const [cloudId, setCloudId] = useAtom(cloudIdAtom);
	const [, setOrgId] = useAtom(orgIdAtom);
	// `availableSitesAtom` is resolved by `LoggedInGate` before this
	// component mounts, so this read never suspends in practice.
	const { data: sites } = useAtomValue(availableSitesAtom);
	const { formatMessage } = useIntl();

	const handleSiteClick = (
		e: MouseEvent<Element> | KeyboardEvent<Element>,
		site: AvailableSite,
	) => {
		e.preventDefault();
		if (site.cloudId) {
			setCloudId(site.cloudId);
			// eslint-disable-next-line no-console
			console.log(`Setting to: ${site.displayName} (${site.cloudId})`);
		}
		if (site.org?.id) {
			setOrgId(site.org.id);
		}
	};

	if (sites.length <= 1) {
		// only show the switcher if there are multiple sites
		return null;
	}

	const currentSite = sites.find((site) => !!site.cloudId && site.cloudId === cloudId);
	const triggerLabel = currentSite?.displayName ?? formatMessage(messages.switchSiteLabel);

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...triggerProps }) => (
				<button
					{...triggerProps}
					ref={triggerRef}
					type="button"
					aria-label={formatMessage(messages.switchSiteLabel)}
					css={styles.trigger}
				>
					{triggerLabel}
					<ChevronDownIcon label="" color="currentColor" />
				</button>
			)}
			shouldRenderToParent
			placement="bottom-end"
		>
			<DropdownItemGroup>
				{sites.map((site) => (
					<DropdownItem
						key={site.cloudId ?? site.url}
						href={site.url}
						isSelected={!!site.cloudId && site.cloudId === cloudId}
						onClick={(e) => handleSiteClick(e, site)}
						testId={
							site.cloudId ? `site-switcher-option-${site.cloudId}` : 'site-switcher-option-unknown'
						}
					>
						{site.displayName}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
